import * as AccordionPrimitive from '@radix-ui/react-accordion';
import React, { type ComponentType, type ReactNode } from 'react';
import { ChevronDownLarge } from '@/components/Icons/ChevronDownLarge';
import { DashboardTileType } from '@/types/components';
import { tv } from '@/utils/tv';

const tile = tv({
  base: 'shadow-card flex flex-col overflow-hidden rounded-xl bg-card',
  variants: {
    isCompact: { true: 'md:h-[370px]', false: 'md:h-full' },
  },
});

interface Props {
  type: DashboardTileType;
  icon: ComponentType;
  title: string;
  badgeCount?: number;
  footerButtonLabel: string;
  hideFooter?: boolean;
  isCompact?: boolean;
  children: ReactNode;
  onFooterButtonClick?: () => void;
}

export const DashboardTile: React.FC<Props> = ({
  type,
  icon: Icon,
  title,
  badgeCount,
  footerButtonLabel,
  hideFooter,
  isCompact,
  children,
  onFooterButtonClick,
}) => {
  const headerInner = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Icon />
        <span className="text-callout-16 truncate text-foreground">
          {title}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!!badgeCount && (
          <span className="text-caption-10 bg-accent flex size-[18px] items-center justify-center rounded-full font-semibold text-white contrast:text-accent-foreground">
            {badgeCount}
          </span>
        )}
        <ChevronDownLarge className="size-6 text-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 md:hidden" />
      </div>
    </>
  );

  return (
    <AccordionPrimitive.Item value={type} className={tile({ isCompact })}>
      <AccordionPrimitive.Header className="flex md:hidden">
        <AccordionPrimitive.Trigger className="group flex w-full cursor-pointer items-center justify-between gap-3 py-4 pr-3 pl-4 text-left outline-none">
          {headerInner}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <div className="hidden w-full items-center justify-between gap-3 py-4 pr-3 pl-4 md:flex">
        {headerInner}
      </div>
      <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down md:flex md:flex-1 md:flex-col md:!h-auto md:!animate-none md:min-h-0">
        <div className="flex flex-col border-t border-border md:flex-1 md:overflow-hidden">
          {children}
        </div>
        {!hideFooter && (
          <div
            data-tile-footer={type}
            className="flex justify-center border-t border-border py-4"
          >
            <button
              type="button"
              onClick={onFooterButtonClick}
              className="text-subhead-14 cursor-pointer text-primary transition-colors hover:text-primary-hover"
            >
              {footerButtonLabel}
            </button>
          </div>
        )}
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
};
